<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import RegionCombo from "../components/RegionCombo.vue";
import DongCombo from "../components/DongCombo.vue";
import { getApiBaseUrl } from "../components/ApiBaseUrl.js";
import { Bar, Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const API_BASE = getApiBaseUrl();

// Form data
const filters = reactive({
  district: "",
  dong: "",
  apartment: "",
  yearRange: "5", // options: '5','3','1','all'
});

// Data states
const areas = ref([]);
const apartments = ref([]);
const trends = ref([]);

const loading = ref(false);
const error = ref("");

// Fetch trends
async function fetchTrends() {
  if (!filters.district || !filters.apartment) {
    error.value = "구, 아파트명을 선택해주세요.";
    trends.value = []; // clear previous data
    return;
  }

  trends.value = [];

  loading.value = true;
  error.value = "";

  try {
    const res = await fetch(`${API_BASE}/api/apartment-trend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        district: filters.district,
        dong: filters.dong || undefined,
        apartment: filters.apartment,
        yearRange: filters.yearRange,
      }),
    });

    if (!res.ok) throw new Error("거래 추이를 불러오지 못했습니다.");
    const data = await res.json();
    trends.value = Array.isArray(data?.trends) ? data.trends : [];
  } catch (err) {
    console.error("거래 추이를 불러오지 못했습니다.:", err);
    error.value = "거래 추이를 불러오지 못했습니다.";
    trends.value = [];
  } finally {
    loading.value = false;
  }
}

// 차트 Y축 폭을 고정하는 공용 플러그인
const fixedYAxisWidth = (fixedWidth = 60) => ({
  id: "fixedYAxisWidth",
  beforeInit(chart) {
    const yScale = chart.options.scales?.y;
    if (!yScale) return;
    const prevAfterFit = yScale.afterFit;
    yScale.afterFit = function (scale) {
      if (typeof prevAfterFit === "function") prevAfterFit.call(this, scale);
      scale.width = fixedWidth; // ← 여기서 고정 폭 지정
    };
  },
});

// Fetch areas for selected apartment
async function fetchAreas() {
  areas.value = [];
  if (!filters.district || !filters.apartment) return;
  try {
    const res = await fetch(`${API_BASE}/api/apartment-areas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        district: filters.district,
        dong: filters.dong || undefined,
        apartment: filters.apartment,
      }),
    });
    if (!res.ok) throw new Error("평형정보를 불러오지 못했습니다.");
    const data = await res.json();
    areas.value = data.areas || [];
  } catch (err) {
    console.error("평형정보를 불러오지 못했습니다.:", err);
    areas.value = [];
  }
}

// ─────────────────────────────────────────────────────────
// 2) 상단 차트 (가격 범위: 최소~최대) - Range Bar
// ─────────────────────────────────────────────────────────
const priceChartData = reactive({
  labels: [],
  datasets: [],
});

const priceChartOptions = reactive({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  layout: { padding: { left: 10 } }, // ← 동일 padding
  scales: {
    y: {
      type: "linear",
      position: "left",
      title: { display: true, text: "가격 (억원)" },
      // ✅ 정수 눈금 강제
      ticks: {
        stepSize: 1, // 눈금 간격을 1로(정수 간격)
        maxTicksLimit: 12, // 너무 많아지면 자동으로 줄이기
        callback: (v) => `${Math.trunc(v)}억`, // 표시는 항상 정수로
      },
      // ticks 위치에 맞춰 경계 계산 (소수 눈금 방지에 도움)
      bounds: "ticks",
    },
    x: {
      ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 12 },
      offset: true,
    },
  },
  plugins: {
    legend: { display: true, position: "top" },
    title: { display: true, text: "월별 평형 가격 범위 (최소가~최대가)" },
    tooltip: {
      /* ...생략... */
    },
  },
});

// ─────────────────────────────────────────────────────────
// 3) 하단 차트 (평형별 거래건수) - Stacked Bar
// ─────────────────────────────────────────────────────────
const volumeChartData = reactive({
  labels: [],
  datasets: [],
});

const volumeChartOptions = reactive({
  responsive: true,
  maintainAspectRatio: false, // ✅ 부모 높이(420px)를 그대로 따르게
  interaction: { mode: "index", intersect: false },
  layout: { padding: { left: 10 } }, // ← 동일 padding
  scales: {
    x: {
      stacked: true,
      ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 12 },
      offset: true, // ⬅ 상단과 동일
    },
    y: {
      stacked: true,
      min: 0,
      title: { display: true, text: "거래건수(건)" },
      ticks: { stepSize: 1, callback: (v) => `${Math.round(v)}건` },
    },
  },
  plugins: {
    legend: { display: true, position: "top" },
    title: { display: true, text: "월별 평형별 거래건수 (스택 막대)" },
    tooltip: { mode: "index", intersect: false },
  },
});

// ─────────────────────────────────────────────────────────
/** 4) 유틸 함수 */
// ─────────────────────────────────────────────────────────

// YYYY-MM 라벨 생성
function ymLabel(r) {
  const y = String(r?.년도 ?? "");
  const m = String(r?.월 ?? "").padStart(2, "0");
  return `${y}-${m}`;
}

// 숫자 변환 유틸(유한수만)
function numOrUndef(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

// 값 배열의 자동 범위(min/max) 계산
function computeExtent(
  values,
  { ratio = 0.05, minPad = 0.1, clampZero = false } = {},
) {
  const nums = (values ?? []).map(numOrUndef).filter((v) => v !== undefined);
  if (!nums.length) return { min: undefined, max: undefined };
  const minVal = Math.min(...nums);
  const maxVal = Math.max(...nums);
  const range = maxVal - minVal;
  const pad = range > 0 ? Math.max(range * ratio, minPad) : minPad;
  let min = minVal - pad;
  let max = maxVal + pad;
  if (clampZero) min = Math.max(0, min);
  return { min, max };
}

// 상·하단 공통 X축 라벨 생성(동일 배열 보장)
function buildCommonLabels() {
  const rows = trends.value ?? [];
  const set = new Set(rows.map(ymLabel));
  return Array.from(set).sort();
}

// 동일 월·평형에 여러 행이 있을 때, 월별 최소/최대를 재집계
function aggregateMonthlyMinMax(rows) {
  const agg = new Map(); // key: `${ym}|${area}` → { lo, hi }
  for (const r of rows) {
    const ym = ymLabel(r);
    const a = numOrUndef(r?.전용면적_평);
    if (a === undefined) continue;

    const lo = numOrUndef(r?.최소가);
    const hi = numOrUndef(r?.최대가);
    const key = `${ym}|${a}`;
    const cur = agg.get(key) ?? {
      lo: Number.POSITIVE_INFINITY,
      hi: Number.NEGATIVE_INFINITY,
    };
    if (Number.isFinite(lo)) cur.lo = Math.min(cur.lo, lo);
    if (Number.isFinite(hi)) cur.hi = Math.max(cur.hi, hi);
    agg.set(key, cur);
  }
  return agg;
}

// 최저가/최대가 디스플레이용 보정 (최저가: truncate, 최대가: round)
function adjustRangeForDisplay(lo, hi) {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo, hi };
  let loAdj = lo;
  let hiAdj = hi;
  // 소수점 존재 여부 판단
  const hasDecimal = (x) => Math.floor(x) !== x;
  if (hasDecimal(loAdj)) loAdj = Math.trunc(loAdj); // 버림
  if (hasDecimal(hiAdj)) hiAdj = Math.round(hiAdj); // 반올림
  // 만약 반올림으로 인해 hi가 lo보다 작아진다면 최소한 같게 맞춤
  if (hiAdj < loAdj) hiAdj = loAdj;
  return { lo: loAdj, hi: hiAdj };
}

// 가격 범위를 시각적으로 보이게 하는 최소 두께 부여(최소가=최대가 시 보정)
function ensureVisibleRange(
  lo,
  hi,
  { abs = 0.05, rel = 0.01, ySpan = 0 } = {},
) {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (hi < lo) [lo, hi] = [hi, lo];
  const minBand = Math.max(abs, ySpan * rel);
  if (hi - lo >= minBand) return [lo, hi];
  const mid = (lo + hi) / 2;
  return [mid - minBand / 2, mid + minBand / 2];
}

// 툴팁 콜백(상단 옵션): 동일가인 경우 "동일가"로 표기
priceChartOptions.plugins.tooltip = {
  mode: "index",
  intersect: false,
  callbacks: {
    label: (ctx) => {
      const ds = ctx.dataset;
      const idx = ctx.dataIndex;
      const orig = Array.isArray(ds._origRanges) ? ds._origRanges[idx] : null;
      const lo = orig?.lo;
      const hi = orig?.hi;
      if (Number.isFinite(lo) && Number.isFinite(hi)) {
        if (lo === hi) return `${ds.label}: ${lo}억 (동일가)`;
        return `${ds.label}: ${lo}억 ~ ${hi}억`;
      }
      const raw = ctx.raw;
      if (Array.isArray(raw)) {
        const [vlo, vhi] = raw;
        if (Number.isFinite(vlo) && Number.isFinite(vhi)) {
          return `${ds.label}: ${vlo}억 ~ ${vhi}억`;
        }
      }
      return `${ds.label}: ${ctx.parsed.y}`;
    },
  },
};

// 가독성 높은 5색 팔레트 (기존 fill + 강조 strongFill)
const PALETTE5 = [
  {
    border: "#2563eb",
    fill: "rgba(37, 99, 235, 0.45)",
    strongFill: "rgba(37, 99, 235, 0.70)",
  },
  {
    border: "#10b981",
    fill: "rgba(16, 185, 129, 0.45)",
    strongFill: "rgba(16, 185, 129, 0.70)",
  },
  {
    border: "#ef4444",
    fill: "rgba(239, 68, 68, 0.45)",
    strongFill: "rgba(239, 68, 68, 0.70)",
  },
  {
    border: "#a855f7",
    fill: "rgba(168, 85, 247, 0.45)",
    strongFill: "rgba(168, 85, 247, 0.70)",
  },
  {
    border: "#f59e0b",
    fill: "rgba(245, 158, 11, 0.45)",
    strongFill: "rgba(245, 158, 11, 0.70)",
  },
];

// ─────────────────────────────────────────────────────────
// 5) 상단/하단 차트 갱신
// ─────────────────────────────────────────────────────────
function updateTopPriceRangeChart() {
  const rows = trends.value ?? [];

  // 1) 공통 X라벨
  const labels = buildCommonLabels();
  priceChartData.labels = labels;

  // 2) 평형 목록(오름차순) 최대 5개
  const areaList = Array.from(
    new Set(
      rows
        .map((r) => numOrUndef(r?.전용면적_평))
        .filter((v) => v !== undefined),
    ),
  )
    .sort((a, b) => a - b)
    .slice(0, 5);

  // 3) 월별 최소/최대 집계 (원본 값 기준)
  const monthlyMinMax = aggregateMonthlyMinMax(rows);

  // 4) (년월, 평형) → 거래건수 합산 맵 (단일 거래 여부 판단용)
  const countsByYM_Area = new Map(); // key: `${ym}|${area}` → 합계
  for (const r of rows) {
    const ym = ymLabel(r);
    const a = numOrUndef(r?.전용면적_평);
    if (a === undefined) continue;
    const c = numOrUndef(r?.거래건수) ?? 0;
    const key = `${ym}|${a}`;
    countsByYM_Area.set(key, (countsByYM_Area.get(key) ?? 0) + c);
  }

  // 5) y축 스팬(디스플레이용 값 기준) 계산 → ε 보정에 사용
  const priceDisplayValues = [];
  for (const [, v] of monthlyMinMax.entries()) {
    if (!Number.isFinite(v.lo) || !Number.isFinite(v.hi)) continue;
    const adj = adjustRangeForDisplay(v.lo, v.hi);
    if (Number.isFinite(adj.lo)) priceDisplayValues.push(adj.lo);
    if (Number.isFinite(adj.hi)) priceDisplayValues.push(adj.hi);
  }
  const ext = computeExtent(priceDisplayValues, { ratio: 0.05, minPad: 0.1 });

  const ySpan =
    Number.isFinite(ext.max) && Number.isFinite(ext.min)
      ? ext.max - ext.min
      : 0;

  // 6) 데이터셋 구성
  const datasets = areaList.map((area, idx) => {
    const { border, fill, strongFill } = PALETTE5[idx % PALETTE5.length];

    // 각 인덱스(월)에 대해 강조 여부를 기록
    const highlightIdx = new Set();

    // (A) 시각용 데이터: ε 보정 (강조 지점은 더 큰 ε)
    const visData = labels.map((ym, i) => {
      const v = monthlyMinMax.get(`${ym}|${area}`);
      if (!v) return null;

      const lo = numOrUndef(v.lo);
      const hi = numOrUndef(v.hi);

      if (lo === undefined || hi === undefined) return null;

      const count = countsByYM_Area.get(`${ym}|${area}`) ?? 0;
      const isSingleEqual = count === 1 && lo === hi;

      // ⬇️ 디스플레이용 값으로 보정 (최저가 truncate, 최대가 round)
      const { lo: loDisp, hi: hiDisp } = adjustRangeForDisplay(lo, hi);

      if (isSingleEqual) {
        // 강조: ε를 더 크게
        highlightIdx.add(i);
        return ensureVisibleRange(loDisp, hiDisp, {
          abs: 0.15,
          rel: 0.02,
          ySpan,
        });
      } else {
        // 일반: 기존 ε
        return ensureVisibleRange(loDisp, hiDisp, {
          abs: 0.05,
          rel: 0.01,
          ySpan,
        });
      }
    });

    // (B) 툴팁을 원본값으로 유지
    const origData = labels.map((ym) => {
      const v = monthlyMinMax.get(`${ym}|${area}`);
      return v
        ? { lo: numOrUndef(v.lo), hi: numOrUndef(v.hi) } // ← 원본 그대로
        : { lo: undefined, hi: undefined };
    });

    // (C) 배경/외곽선 두께를 강조 지점에서만 변경
    const backgroundColor = labels.map((_, i) =>
      highlightIdx.has(i) ? strongFill : fill,
    );
    const borderWidth = (ctx) => (highlightIdx.has(ctx.dataIndex) ? 3 : 2);

    return {
      label: `${area}평`,
      type: "bar",
      yAxisID: "y",
      data: visData,
      _origRanges: origData, // 툴팁 원본 값 표기용

      borderColor: border,
      backgroundColor,
      borderWidth,
      borderRadius: 3,

      // ✅ 막대 두께를 '픽셀'로 고정
      barThickness: 12, // ← 원하는 픽셀 값(12~16 권장)
      maxBarThickness: 12, // 안전하게 동일 값
      categoryPercentage: 1.0, // (선택) 영향 최소화를 위해 1.0로
      barPercentage: 1.0, // (선택) 영향 최소화를 위해 1.0로
    };
  });

  priceChartData.datasets.splice(
    0,
    priceChartData.datasets.length,
    ...datasets,
  );

  // 7) y축 범위: 디스플레이용 값 기준으로 자동 설정
  const yMin = Number.isFinite(ext.min) ? Math.floor(ext.min) : undefined;
  const yMax = Number.isFinite(ext.max) ? Math.ceil(ext.max) : undefined;
  priceChartOptions.scales.y.min = yMin;
  priceChartOptions.scales.y.max = yMax;
}

function updateBottomAreaCountsStackedBar() {
  const rows = trends.value ?? [];

  // 1) 공통 X라벨
  const labels = buildCommonLabels();
  volumeChartData.labels = labels;

  // 2) 평형 목록(오름차순) 최대 5개
  const areaList = Array.from(
    new Set(
      rows
        .map((r) => numOrUndef(r?.전용면적_평))
        .filter((v) => v !== undefined),
    ),
  )
    .sort((a, b) => a - b)
    .slice(0, 5);

  // 3) (년월, 평형) → 거래건수 합산(복수행 대비)
  const countsByYM_Area = new Map(); // key: `${ym}|${area}` → count 합계
  for (const r of rows) {
    const ym = ymLabel(r);
    const a = numOrUndef(r?.전용면적_평);
    if (a === undefined) continue;
    const c = numOrUndef(r?.거래건수) ?? 0;
    const key = `${ym}|${a}`;
    countsByYM_Area.set(key, (countsByYM_Area.get(key) ?? 0) + c);
  }

  // 4) 스택 막대 데이터셋(평형별)
  const volDatasets = areaList.map((area, idx) => {
    const { border, fill } = PALETTE5[idx % PALETTE5.length];
    return {
      label: `${area}평`,
      type: "bar",
      data: labels.map((ym) => {
        const v = countsByYM_Area.get(`${ym}|${area}`);
        return Number.isFinite(v) ? v : 0;
      }),
      backgroundColor: fill,
      borderColor: border,
      borderWidth: 1,

      // ✅ 막대 두께를 '픽셀'로 고정
      barThickness: 12, // ← 원하는 픽셀 값(12~16 권장)
      maxBarThickness: 12, // 안전하게 동일 값
      categoryPercentage: 1.0, // (선택) 영향 최소화를 위해 1.0로
      barPercentage: 1.0, // (선택) 영향 최소화를 위해 1.0로
    };
  });

  volumeChartData.datasets.splice(
    0,
    volumeChartData.datasets.length,
    ...volDatasets,
  );

  // 5) y 최대값 자동: 스택 합계 기준
  const stackedMonthly = labels.map((ym, i) =>
    volDatasets.reduce(
      (sum, ds) => sum + (Number.isFinite(ds.data[i]) ? ds.data[i] : 0),
      0,
    ),
  );
  const maxStacked = stackedMonthly.length ? Math.max(...stackedMonthly) : 0;
  volumeChartOptions.scales.y.max =
    maxStacked + Math.max(1, Math.ceil(maxStacked * 0.08));
}

// 트렌드 데이터가 바뀌면 두 차트 동시 갱신
watch(trends, () => {
  updateTopPriceRangeChart();
  updateBottomAreaCountsStackedBar();
});

// Watch district change and reset dong/apartment
watch(
  () => filters.district,
  () => {
    filters.dong = "";
    filters.apartment = "";
    apartments.value = [];
    areas.value = [];
  },
);

// Watch dong change and fetch apartments
watch(
  () => filters.dong,
  async (newDong) => {
    if (!newDong) {
      apartments.value = [];
      filters.apartment = "";
      return;
    }

    loading.value = true;
    try {
      const res = await fetch(`${API_BASE}/api/apartments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          district: filters.district,
          dong: newDong,
          yearRange: filters.yearRange,
        }),
      });
      if (!res.ok) throw new Error("Failed to fetch apartments");
      const data = await res.json();

      const list = data.apartments || [];
      apartments.value = (Array.isArray(list) ? list : [])
        .map((apt) => {
          if (!apt) return null;
          if (typeof apt === "string") return apt;
          // prefer known keys, fallback to first value
          return apt.단지명 || apt.단지 || Object.values(apt)[0] || null;
        })
        .filter(Boolean);
      filters.apartment = "";
      areas.value = [];
    } catch (err) {
      console.error("Error fetching apartments:", err);
      apartments.value = [];
    } finally {
      loading.value = false;
    }
  },
);

// 초기 마운트 시 데이터가 있으면 한 번 갱신
onMounted(() => {
  if (Array.isArray(trends.value) && trends.value.length) {
    updateTopPriceRangeChart();
    updateBottomAreaCountsStackedBar();
  }
});

// Computed for display
const hasData = computed(() => trends.value.length > 0);
</script>

<template>
  <div class="wrap">
    <div class="panel">
      <h2>실거래 추이</h2>
      <div class="filters">
        <label>
          지역
          <RegionCombo v-model="filters.district" />
        </label>
        <label>
          지역(동)
          <DongCombo v-model="filters.dong" :district="filters.district" />
        </label>
        <label>
          직전연도
          <select v-model="filters.yearRange" class="combo-select">
            <option value="5">5개년</option>
            <option value="3">3개년</option>
            <option value="1">1개년</option>
            <option value="all">전체</option>
          </select>
        </label>
        <label>
          <span>
            아파트명
            <span v-if="loading" class="muted">로딩...</span>
          </span>
          <select v-model="filters.apartment" class="combo-select">
            <option value="">선택</option>
            <option v-for="apt in apartments" :key="apt" :value="apt">
              {{ apt }}
            </option>
          </select>
        </label>

        <div style="display: flex; gap: 8px; align-items: center">
          <button class="btn" @click="fetchTrends" :disabled="loading">
            {{ loading ? "조회중..." : "조회" }}
          </button>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <!-- ✅ 차트·메시지를 감싸는 스크롤 영역 -->
      <div class="charts-scroll">
        <!-- 상단 차트 -->

        <!-- 상단: 가격 범위 (최소~최대) -->
        <div
          v-if="hasData"
          class="chart-wrapper"
          style="height: 340px; border: 1px solid #e5e7eb; border-radius: 8px"
        >
          <Bar :data="priceChartData" :options="priceChartOptions" />
        </div>

        <!-- 하단: 거래건수 -->
        <div
          v-if="hasData"
          class="chart-wrapper"
          style="height: 210px; border: 1px solid #e5e7eb; border-radius: 8px"
        >
          <Bar :data="volumeChartData" :options="volumeChartOptions" />
        </div>

        <p v-else class="empty">
          조회 결과가 없습니다. 조건을 입력하고 조회해 보세요.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* main.css 또는 App.vue의 <style> (scoped 아님) */

/* 전역: 뷰포트 전체 높이 확보 */
:global(html, body, #app) {
  margin: 0;
  padding: 0;
  height: 100dvh; /* 동적 뷰포트 높이 (iOS 주소창 대응) */
  min-height: 100dvh;
  overflow: hidden; /* 페이지 전체 스크롤은 끄고 내부에서만 스크롤 */
}

/* (선택) 모바일/사파리에서 관성 스크롤 */
body {
  -webkit-overflow-scrolling: touch;
}

/* 상위 컨테이너에 고정 높이를 주지 마세요 (스크롤 막힘) */

/* 페이지 상위 컨테이너 */
.wrap {
  max-width: 99.5%;
  height: 100%; /* #app의 100dvh 채움 */
  display: flex;
  flex-direction: column;
  min-height: 0; /* ✅ 내부 스크롤 동작에 필수 */
  margin: 0 auto; /* 필요 시 중앙 정렬 */
}

/* 패널은 남은 공간을 모두 차지하고 내부에서 스크롤을 분배 */
.panel {
  padding: 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);

  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0; /* ✅ 필수 */
}

/* ✅ 차트들이 들어가는 스크롤 영역 */
.charts-scroll {
  flex: 1 1 auto; /* 남은 공간을 전부 차지 */
  min-height: 0; /* ✅ 필수 */
  overflow-y: auto; /* 이 영역만 세로 스크롤 */
  padding-bottom: 8px; /* 마지막 요소가 가려지지 않게 여유 */
  -webkit-overflow-scrolling: touch; /* iOS 관성 스크롤 */
}

/* 차트 박스 간격 */
.chart-wrapper {
  margin-top: 14px;
  padding: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* 필터 영역은 고정 높이(내용만큼) */
.filters {
  display: grid;
  grid-template-columns: 0.5fr repeat(4, 1fr);
  gap: 10px;
  align-items: end;
  margin-top: 12px;
  flex: 0 0 auto; /* ✅ 고정 */
}

label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  opacity: 0.95;
}
input,
select {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.18);
  color: #eaf2ff;
}
input::placeholder {
  color: rgba(234, 242, 255, 0.5);
}
.btn {
  height: 40px;
  border-radius: 12px;
  border: 0;
  background: #2d8cff;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  width: 100%;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.error {
  color: #ffb3b3;
  margin-top: 10px;
}
.chart-wrapper {
  margin-top: 14px;
  padding: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.empty {
  margin-top: 14px;
  opacity: 0.8;
}
@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr 1fr;
  }
}

/* ===== Tablet Responsive (768px and below) ===== */
@media (max-width: 768px) {
  .panel {
    padding: 14px;
  }

  h2 {
    font-size: 1.4em;
    margin-bottom: 12px;
  }

  .filters {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 10px;
  }

  label {
    gap: 4px;
    font-size: 12px;
  }

  input,
  select {
    padding: 10px;
    font-size: 15px;
    min-height: 44px;
  }

  .btn {
    height: 44px;
    font-size: 14px;
  }

  .chart-wrapper {
    padding: 10px;
    margin-top: 10px;
  }

  .charts-scroll {
    padding-bottom: 6px;
  }

  .combo-select {
    min-height: 44px;
  }
}

/* ===== Mobile Responsive (480px and below) ===== */
@media (max-width: 480px) {
  :global(html, body, #app) {
    height: 100dvh;
  }

  .wrap {
    max-width: 100%;
  }

  .panel {
    padding: 10px;
    border-radius: 8px;
  }

  h2 {
    font-size: 1.2em;
    margin-top: 0;
    margin-bottom: 10px;
  }

  .filters {
    grid-template-columns: 1fr;
    gap: 6px;
    margin-top: 8px;
  }

  label {
    gap: 3px;
    font-size: 11px;
  }

  input,
  select {
    padding: 10px;
    font-size: 16px;
    min-height: 44px;
  }

  .btn {
    height: 44px;
    font-size: 13px;
    width: 100%;
  }

  .chart-wrapper {
    padding: 8px;
    margin-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .charts-scroll {
    padding-bottom: 4px;
  }

  .error {
    font-size: 12px;
  }

  .combo-select {
    width: 100%;
    min-height: 44px;
    padding: 10px;
    font-size: 14px;
  }

  .combo-select option {
    font-size: 14px;
  }

  .muted {
    font-size: 10px;
  }
}

/* Apartment combo styles matching RegionCombo */
.combo-select {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.16);
  color: #ffffff;
}
.combo-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.combo-select option {
  color: #111827;
  background: #ffffff;
}
.muted {
  opacity: 0.8;
  font-size: 12px;
  display: inline;
}
.error {
  color: #ffb3b3;
  font-size: 12px;
}
</style>
